import { useReportingScheduler } from "@/entities/reportingScheduler/hooks";
import { asSchedule } from "@/entities/reportingScheduler/helpers";

/**
 * The report row behind a chat answer, and that same row as a schedule.
 *
 * A scheduled answer names its row by `scheduleId` and a saved one by
 * `savedReportId`; saving and scheduling are one row, so either finds it. The
 * schedule modal needs the row even before it has a cadence, so that a first
 * schedule keeps the report's own name and origin rather than the answer's.
 */
export const useMessageReportRow = (
  scheduleId: string | null | undefined,
  savedReportId: string | null | undefined
) => {
  const rowId = scheduleId ?? savedReportId;
  const { data: row = null, isPending, isError } = useReportingScheduler(rowId);
  // Until the row is in hand, a schedule would fall back to the answer's own
  // name and message, which is what loading the row exists to prevent.
  const isAwaitingRow = !!rowId && !row;
  return {
    row,
    schedule: asSchedule(row),
    // Pending, not loading: a fetch paused offline is pending without loading.
    isRowLoading: isAwaitingRow && isPending,
    // A deleted saved report settles as null; saving would write onto its id.
    isRowFailed: isAwaitingRow && (isError || (!isPending && !!savedReportId))
  };
};
